import React, { useState, useEffect, useMemo } from 'react'
import { UserAddIcon } from '@heroicons/react/outline'
import { tagHotjarRecording, isActive } from '../../helpers/util'
import Button from '../../components/Button/Button'
import { sendInvite } from '../../store/actions/actions'
import useUsers from '../../hooks/useUsers'
import useAuth from '../../hooks/useAuth'
import { listUsers } from '../../repositories/UsersRepository'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import InviteCoworkerModal from '../../components/InviteCoworkerModal/InviteCoworkerModal'
import Table from '../../components/Table/Table'
import { MailOpenIcon, TrashIcon } from '@heroicons/react/outline'
import useAlert from '../../hooks/useAlert'
import { formatUsersRows, usersCols } from '../../helpers/tableUtils'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useScreen from '../../hooks/useScreen'

const iconClasses = 'w-5 h-5 stroke-2 stroke-current text-textDarkGray'

const UsersPage = () => {
    const [userList, setUserList] = useState([])
    const [listSize, setListSize] = useState(0)
    const [error, setError] = useState('')
    const [modalMobile, setModalMobile] = useState(false)
    const { handleDeleteUser } = useUsers()
    const { role, user } = useAuth()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const { changeLoading } = useGlobalLoading()
    const { isMobile, isTablet } = useScreen()

    const onListUsersError = error => {
        tagHotjarRecording('API Error')
        setError(
            error?.response?.data?.message ??
            error?.response?.message ??
            error?.message,
        )
    }

    const companyIdForFetch =
        user?.company?.id != null && user?.company?.id !== ''
            ? String(user.company.id)
            : null

    useEffect(() => {
        if (companyIdForFetch == null) {
            return
        }

        let active = true
        changeLoading(true)

        listUsers(companyIdForFetch)
            .then(response => {
                if (!active) {
                    return
                }
                const users = response?.data?.users ?? []
                const next = Array.isArray(users) ? users : []
                setUserList(next)
                setListSize(next.length)
            })
            .catch(err => {
                if (!active) {
                    return
                }
                onListUsersError(err)
            })
            .finally(() => {
                if (active) {
                    changeLoading(false)
                }
            })

        return () => {
            active = false
            changeLoading(false)
        }
    }, [companyIdForFetch])

    const onDeletedSuccess = info => {
        const userId = info?.userId
        const updatedUsers = userList.filter(u => u.id !== userId)
        setUserList(updatedUsers)
        setListSize(updatedUsers.length)
        setError('')
        handleChangeAlert({
            title: 'User Deleted',
            message: 'User deleted correctly',
            type: 'success',
        })
        changeLoading(false)
    }

    const onDeletedError = err => {
        tagHotjarRecording('API Error')
        const {
            message = 'Please check your internet connection and try again.',
        } = err?.response?.data ?? {}
        setError(message)
        changeLoading(false)
    }

    const removeUser = userId => {
        const paramsToDelete = {
            userId,
            adminId: user?.id,
        }
        changeLoading(true)
        handleDeleteUser(paramsToDelete, onDeletedSuccess, onDeletedError)
    }

    const sendInvitationMail = userId => {
        changeLoading(true)
        sendInvite(userId)
            .then(() => {
                changeLoading(false)
                createInvite(userId, 'invite')
                handleChangeAlert({
                    title: 'Invite sent',
                    type: 'success',
                })
            })
            .catch(error => {
                changeLoading(false)
                handleChangeAlert({
                    title: null,
                    message: error?.response?.data?.message,
                    type: 'error',
                })
            })
    }

    const createInvite = (userId, type) => {
        let users = JSON.parse(JSON.stringify(userList))
        let index = users.findIndex(u => u.id === userId)
        if (index === -1 || !users[index]?.invites) {
            return
        }
        if (users[index].invites.length == 0) {
            let invite = { user_id: userId, type: type }
            users[index].invites.push(invite)
            setUserList(users)
        }
    }

    const onUserInvited = userInfo => {
        let newUserList = [...userList]
        newUserList?.push({
            ...userInfo,
            invites: [{ user_id: userInfo?.id, type: 'invite' }],
        })
        setUserList(newUserList)
        setListSize(newUserList.length)
    }

    error ? console.log(error) : null

    const currentUserForTable = useMemo(() => {
        if (!user || typeof user !== 'object') {
            return null
        }
        const userLocation = user?.locationPreferences?.find(
            location => location.id === user.primary_location_id,
        )
        return {
            ...user,
            shipping_name: userLocation?.label,
            shipping_address: userLocation?.address,
        }
    }, [user])

    const tableRows = useMemo(() => {
        if (!currentUserForTable) {
            return []
        }
        return formatUsersRows(userList, currentUserForTable)
    }, [userList, currentUserForTable])

    const defaultOptions = useMemo(
        () => [
            {
                label: 'Send Invite',
                icon: <MailOpenIcon className={iconClasses} />,
                onClick: rowUser => {
                    sendInvitationMail(rowUser?.id)
                },
                isValid: rowUser => !isActive(rowUser),
            },
            {
                label: 'Remove',
                icon: <TrashIcon className={iconClasses} />,
                onClick: userRow => {
                    handleChangeAlert({
                        title: null,
                        message:
                            "Are you sure you want to remove this user? This action can't be undone.",
                        type: 'error',
                        textOk: 'Yes, I am sure',
                        textCancel: 'No, cancel',
                        onDone: () => removeUser(userRow?.id),
                        onCancel: () => handleChangeOpen(false),
                    })
                },
                isValid: rowUser =>
                    rowUser?.id !== user?.id && role === 'manager',
            },
        ],
        [
            role,
            user?.id,
            handleChangeAlert,
            handleChangeOpen,
            userList,
        ],
    )

    const headerActions = () => {
        if (isMobile || isTablet) {
            return (
                <Button
                    variant="primary"
                    onClick={() => setModalMobile(true)}
                    text="Invite Coworkers"
                    className="md:w-auto w-full"
                    icon={
                        <UserAddIcon className="text-yellow stroke-2 w-6 h-6 mr-2" />
                    }
                />
            )
        } else {
            return (
                <InviteCoworkerModal
                    onUserAdded={onUserInvited}
                    key="inviteCowoker"
                    buttonText="Invite Coworkers"
                    buttonVariant="primary"
                />
            )
        }
    }

    return (
        <ContentLayout>
            {modalMobile ? (
                <InviteCoworkerModal
                    onUserAdded={onUserInvited}
                    key="inviteCowoker"
                    buttonText="Invite Coworkers"
                    buttonVariant="primary"
                    showMobile
                    onCancel={() => setModalMobile(false)}
                />
            ) : (
                <>
                    <MainHeading title="Users" actions={[headerActions()]} />
                    <div className="lg:mt-4 w-full flex-grow lg:px-6 lg:pb-6">
                        <Table
                            hasSearch={false}
                            columns={usersCols}
                            data={tableRows}
                            setRowsSize={setListSize}
                            totalResults={listSize || tableRows?.length}
                            hasOptions={true}
                            options={defaultOptions}
                            headerTitle="User Details"
                            searchPlaceholder="Search User"
                            type="scroll"
                            fitContentHeight
                            searchFields={[
                                'userNameKey',
                                'statusKey',
                                'email',
                                'phone',
                                'phoneKey',
                                'shipping_name',
                                'shipping_address',
                                'locationKey',
                            ]}
                        />
                    </div>
                </>
            )}
        </ContentLayout>
    )
}

export default UsersPage
